import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import EmailSchedule from "@mapmycustomers/shared/types/email/EmailSchedule";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import { doesNeedToOpenActivityFormAfterSendingEmail } from "@app/store/email";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import EmailContext from "../EmailContext";
import EmailSendAndCreateActivitiesButton from "../EmailSendAndCreateActivitiesButton";

import styles from "./EmailComposeModal.module.scss";
import messages from "./messages";

interface EmailActionButtonProps {
  emailSending: boolean;
  onClick: () => void;
  onScheduleChange?: (schedule: EmailSchedule | undefined) => void;
  schedule?: EmailSchedule;
}

const EmailActionButton = ({
  emailSending,
  needOpenActivityModal,
  onClick,
  onScheduleChange,
  schedule,
}: EmailActionButtonProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const { emailFilled, entities, templateChanged, validationError } =
    useNonNullableContext(EmailContext);

  const disabledTooltip = useMemo(() => {
    if (validationError) {
      return validationError;
    }

    if (!emailFilled || !entities.length) {
      return intl.formatMessage({
        id: "email.creation.nextDisabledTooltip",
        defaultMessage: "Fill out subject, body and recipient to send email.",
        description: "Next disabled tooltip",
      });
    }
  }, [intl, emailFilled, entities, validationError]);

  const disabled = emailSending || !!disabledTooltip;

  const handleClick = useCallback(() => {
    const analyticPart = needOpenActivityModal ? "Next" : "Send and Create";
    analytics.clicked(["Email action"], { action: analyticPart });
    onClick();
  }, [analytics, onClick, needOpenActivityModal]);

  return (
    <Popconfirm
      cancelText={intl.formatMessage({
        id: "email.creation.nextConfirmationNo",
        defaultMessage: "Cancel",
        description: "Next confirmation no",
      })}
      description={intl.formatMessage(messages.nextConfirmationTitle)}
      disabled={!templateChanged}
      okText={intl.formatMessage(messages.nextConfirmationYes)}
      onConfirm={handleClick}
      overlayClassName={styles.confirmation}
    >
      <span>
        {needOpenActivityModal ? (
          <Tooltip title={disabledTooltip}>
            <span>
              <Button
                disabled={disabled}
                leftIcon={<FontAwesomeIcon icon={faArrowRight} />}
                loading={emailSending}
                onClick={templateChanged ? undefined : handleClick}
                type="primary"
              >
                {intl.formatMessage(messages.next)}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <EmailSendAndCreateActivitiesButton
            disabled={disabled}
            loading={emailSending}
            onClick={templateChanged ? undefined : handleClick}
            onScheduleChange={onScheduleChange}
            recipientCount={entities.length}
            schedule={schedule}
            tooltipTitle={disabledTooltip}
          />
        )}
      </span>
    </Popconfirm>
  );
};

const mapStateToProps = (state: RootState) => ({
  needOpenActivityModal: doesNeedToOpenActivityFormAfterSendingEmail(state),
});

export default connect(mapStateToProps)(EmailActionButton);
